import { useEffect } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchFunnelResultReasons,
  FunnelResultReason,
  funnelsSelector,
  moveOpportunityToLoss,
  MoveOpportunityToLossPayload,
} from 'store/reducers/funnels';
import emptyStringToNull from 'utils/emptyStringToNull';

const validationSchema = yup.object({
  resultReason: yup.object().nullable(),
});

type FormValues = {
  resultReason: FunnelResultReason | null;
};

interface MarkOpportunityAsLossConfirmProps {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  opportunityIds: Array<string>;
  onSuccess: () => void;
}

function MarkOpportunityAsLossConfirm(
  props: MarkOpportunityAsLossConfirmProps,
) {
  const { open, onClose, funnelId, opportunityIds, onSuccess } = props;
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { resultReasons, loadingResultReasons } =
    useAppSelector(funnelsSelector);

  useEffect(() => {
    if (open) {
      dispatch(fetchFunnelResultReasons());
    }
  }, [dispatch, open]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      resultReason: null,
    },
    validationSchema,
    onSubmit: async (values: FormValues) => {
      const payload: MoveOpportunityToLossPayload = {
        funnelId,
        opportunityIds,
        lossResultReasonId: emptyStringToNull(values.resultReason?.id),
      };

      await dispatch(moveOpportunityToLoss(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Operação realizada com sucesso.');
          onSuccess();
          onClose();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const handleClose = () => {
    if (!formik.isSubmitting) onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onExited: () => {
          formik.resetForm();
        },
      }}
    >
      <DialogHeader title="Marcar como Perda" onClose={handleClose} />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2">
                Tem certeza de que deseja marcar esta oportunidade como perdida?
                Essa ação moverá a oportunidade para a etapa de Perdas.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id="resultReason"
                options={
                  resultReasons.filter(
                    resultReason => resultReason.type === 'LOST',
                  ) || []
                }
                loading={loadingResultReasons}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={formik.values.resultReason}
                onChange={(_, newValue, reason) => {
                  if (newValue) formik.setFieldValue('resultReason', newValue);

                  if (reason === 'clear') {
                    formik.setFieldValue('resultReason', null);
                  }
                }}
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                noOptionsText={
                  <Typography variant="body2">
                    Nenhum motivo encontrado
                  </Typography>
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Motivo da perda"
                    variant="outlined"
                    placeholder="Pesquise"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            style={{ backgroundColor: theme.palette.error.main }}
            disabled={formik.isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
          >
            Confirmar
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

export default MarkOpportunityAsLossConfirm;
